/**
 * PlansPage.jsx
 * Parent-facing page: list all plans for a child, with hide/show/delete controls.
 * Reached via /vocab/parent/child/:childId/plans
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Plus, DotsThree, Eye, EyeSlash,
  Trash, CalendarBlank, PuzzlePiece, Warning, PencilSimple,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge }  from "@/components/styleguide/ui/BloomBadge";
import { Mascot }      from "@/components/styleguide/Mascot";
import { cn }          from "@/lib/utils";
import { api }         from "./api";
import { iconConfig }  from "./planIcons";

/* ── Three-dot menu per plan ────────────────────────────────────────────── */
function PlanMenu({ plan, onEdit, onHideToggle, onDelete, saving }) {
  const [open,          setOpen]          = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (confirmDelete) {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground">Delete?</span>
        <button
          onClick={() => { onDelete(); setConfirmDelete(false); }}
          disabled={saving}
          className="text-xs font-extrabold text-destructive hover:underline disabled:opacity-50"
        >
          Yes
        </button>
        <button
          onClick={() => setConfirmDelete(false)}
          className="text-xs font-bold text-muted-foreground hover:underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Plan options"
        aria-expanded={open}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border"
      >
        <DotsThree size={18} weight="bold" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1,    y: 0  }}
            exit={{   opacity: 0, scale: 0.95, y: -4  }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-10 z-30 min-w-[190px] overflow-hidden rounded-2xl border border-border bg-card shadow-soft-lg"
          >
            <button
              onClick={() => { onEdit(); setOpen(false); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-foreground transition hover:bg-muted/50"
            >
              <PencilSimple size={16} className="shrink-0 text-muted-foreground" /> Edit plan
            </button>
            <div className="mx-3 h-px bg-border" />
            <button
              onClick={() => { onHideToggle(); setOpen(false); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-foreground transition hover:bg-muted/50"
            >
              {plan.status === "active"
                ? <><EyeSlash size={16} className="shrink-0 text-muted-foreground" /> Hide from child</>
                : <><Eye      size={16} className="shrink-0 text-muted-foreground" /> Show to child</>
              }
            </button>
            <div className="mx-3 h-px bg-border" />
            <button
              onClick={() => { setOpen(false); setConfirmDelete(true); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-destructive transition hover:bg-destructive/5"
            >
              <Trash size={16} className="shrink-0" /> Delete plan
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Plan row ───────────────────────────────────────────────────────────── */
function PlanRow({ plan, onEdit, onHideToggle, onDelete, saving }) {
  const { Icon, bg, text } = iconConfig(plan.icon);
  const { quiz_count, avg_score } = plan.stats ?? {};

  return (
    <div className="rounded-2xl border border-border bg-card p-4 transition">
      <div className="flex items-start gap-3">
        {/* Icon + Info — dimmed when hidden; menu stays at full opacity */}
        <div className={cn("flex min-w-0 flex-1 items-start gap-3", plan.status === "hidden" && "opacity-60")}>
          {/* Icon */}
          <span className={cn("ic mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full", bg)}>
            <Icon size={18} weight="fill" className={text} />
          </span>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <p className="font-display text-sm font-black text-foreground">{plan.name}</p>
              <BloomBadge variant="neutral" className="px-2 py-0 text-[10px]">
                {plan.word_ids.length} words
              </BloomBadge>
              {plan.status === "hidden" && (
                <BloomBadge variant="neutral" className="flex items-center gap-1 px-2 py-0 text-[10px]">
                  <EyeSlash size={9} /> Hidden
                </BloomBadge>
              )}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <PuzzlePiece size={11} weight="fill" />
                {quiz_count > 0
                  ? `${quiz_count} quiz${quiz_count !== 1 ? "zes" : ""} done`
                  : "No quizzes yet"}
              </span>
              {avg_score != null && (
                <span className="text-xs font-semibold text-muted-foreground">
                  Avg score: <strong className="text-foreground">{avg_score}%</strong>
                </span>
              )}
              <span className="text-xs font-semibold text-muted-foreground">
                {plan.quiz_count} Qs/session
              </span>
            </div>
          </div>
        </div>

        {/* Menu — outside the opacity wrapper, always full opacity */}
        <PlanMenu
          plan={plan}
          onEdit={onEdit}
          onHideToggle={onHideToggle}
          onDelete={onDelete}
          saving={saving}
        />
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────── */
export default function PlansPage() {
  const { childId } = useParams();
  const navigate    = useNavigate();

  const [child,   setChild]   = useState(null);
  const [plans,   setPlans]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [children, plansData] = await Promise.all([
        api("/children"),
        api(`/children/${childId}/plans`),
      ]);
      setChild(children.find(c => c.child_id === childId) ?? null);
      setPlans(plansData);
    } catch {
      setError("Couldn't load plans. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => { load(); }, [load]);

  const handleHideToggle = async (plan) => {
    const nextStatus = plan.status === "active" ? "hidden" : "active";
    // Optimistic update
    setPlans(ps => ps.map(p => p.plan_id === plan.plan_id ? { ...p, status: nextStatus } : p));
    setSaving(true);
    try {
      await api(`/children/${childId}/plans/${plan.plan_id}`, {
        method: "PATCH",
        body: { status: nextStatus },
      });
    } catch {
      // Revert on failure
      setPlans(ps => ps.map(p => p.plan_id === plan.plan_id ? { ...p, status: plan.status } : p));
      setError("Couldn't update plan. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    setSaving(true);
    try {
      await api(`/children/${childId}/plans/${plan.plan_id}`, { method: "DELETE" });
      setPlans(ps => ps.filter(p => p.plan_id !== plan.plan_id));
    } catch {
      setError("Couldn't delete plan. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const firstName = child?.first_name ?? "…";

  return (
    <div className="min-h-[100svh] bg-background">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate("/vocab/parent")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border"
            aria-label="Back to dashboard"
          >
            <ArrowLeft size={16} weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg font-black text-foreground">
              Plans for {firstName}
            </p>
            {plans && (
              <p className="text-xs font-semibold text-muted-foreground">
                {plans.length} plan{plans.length !== 1 ? "s" : ""}
                {plans.length > 0 && ` · ${plans.reduce((s, p) => s + p.word_ids.length, 0)} words total`}
              </p>
            )}
          </div>
          <BloomButton
            size="sm"
            leftIcon={Plus}
            onClick={() => navigate(`/vocab/parent/child/${childId}/plans/create`)}
          >
            Create
          </BloomButton>
        </div>
      </div>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
            <Warning size={16} weight="fill" className="shrink-0" />
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        )}

        {/* Plans list */}
        {!loading && plans && (
          <>
            {plans.length > 0 ? (
              <div className="space-y-3">
                {plans.map(plan => (
                  <PlanRow
                    key={plan.plan_id}
                    plan={plan}
                    onEdit={() => navigate(`/vocab/parent/child/${childId}/plans/${plan.plan_id}/edit`)}
                    onHideToggle={() => handleHideToggle(plan)}
                    onDelete={() => handleDelete(plan)}
                    saving={saving}
                  />
                ))}
              </div>
            ) : (
              /* Empty state */
              <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-border px-6 py-12 text-center">
                <Mascot size={64} mood="happy" animated={false} />
                <div>
                  <p className="font-display text-lg font-black text-foreground">
                    No plans yet
                  </p>
                  <p className="mt-1 text-sm font-semibold text-muted-foreground">
                    Create a plan to give {firstName} a focused set of words to study.
                  </p>
                </div>
              </div>
            )}

            {/* Create CTA */}
            <button
              onClick={() => navigate(`/vocab/parent/child/${childId}/plans/create`)}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border px-4 py-4 text-sm font-extrabold text-muted-foreground transition hover:border-primary/40 hover:text-primary-strong"
            >
              <Plus size={16} weight="bold" />
              Create a new plan
            </button>
          </>
        )}
      </main>
    </div>
  );
}